import { validateGameScore } from '@/lib/scoring';
import { tryResolveAppState } from '@/lib/scorecard/resolve';
import { isValidPointsPerGame, type GameFormat } from '@/types/game-format';
import type { Tables } from '@/types/supabase';
import type { ScoreAppState } from '../score/components/types';
import { pointsForGame } from './gameTarget';

/**
 * Canonical retirement rules: a player retires mid-match, the partial score is kept and the
 * opponent wins. Mirrored in SQL and Dart; both are pinned by test/fixtures/scorecard/retired-cases.json.
 */

export type Side = 'player1' | 'player2';
export interface PlainGame {
  player1: number;
  player2: number;
}
export type RetiredReason =
  | 'no_rally'
  | 'invalid_game'
  | 'already_won'
  | 'retiree_is_winner'
  | 'too_many_games';
export type RetiredVerdict =
  | {
      ok: true;
      completed: PlainGame[];
      partial: PlainGame | null;
      winnerSide: Side;
      played: { player1: number; player2: number };
    }
  | { ok: false; reason: RetiredReason };

function isReachableMidGame(
  g: PlainGame,
  target: number,
  deuceRule: GameFormat['deuceRule'],
): boolean {
  if (![g.player1, g.player2].every((n) => Number.isInteger(n) && n >= 0)) return false;
  const hi = Math.max(g.player1, g.player2);
  const lo = Math.min(g.player1, g.player2);
  if (hi < target) return true;
  return deuceRule !== 'sudden_death' && lo >= target - 1 && hi - lo <= 1;
}

export function validateRetiredGames(
  games: PlainGame[],
  format: GameFormat,
  retiredSide: Side,
): RetiredVerdict {
  if (games.every((g) => g.player1 === 0 && g.player2 === 0)) {
    return { ok: false, reason: 'no_rally' };
  }
  // The score grid submits bestOf slots; drop every trailing 0-0 placeholder.
  let end = games.length;
  while (end > 0 && games[end - 1].player1 === 0 && games[end - 1].player2 === 0) end--;
  const trimmed = games.slice(0, end);
  if (trimmed.length > format.bestOf) return { ok: false, reason: 'too_many_games' };

  const scoreOf = (g: PlainGame, index: number) =>
    validateGameScore(g.player1, g.player2, pointsForGame(format, index + 1), format.deuceRule);

  const completed: PlainGame[] = [];
  let partial: PlainGame | null = null;
  for (let i = 0; i < trimmed.length; i++) {
    const g = trimmed[i];
    if (scoreOf(g, i).valid) {
      completed.push(g);
      continue;
    }
    const isLast = i === trimmed.length - 1;
    const target = pointsForGame(format, i + 1);
    if (!isLast || !isReachableMidGame(g, target, format.deuceRule)) {
      return { ok: false, reason: 'invalid_game' };
    }
    partial = g;
  }

  const played = { player1: 0, player2: 0 };
  completed.forEach((g, i) => {
    const winner = scoreOf(g, i).winner;
    if (winner) played[winner] += 1;
  });
  const winnerSide: Side = retiredSide === 'player1' ? 'player2' : 'player1';
  const needed = Math.ceil(format.bestOf / 2);
  if (played.player1 >= needed || played.player2 >= needed) {
    return { ok: false, reason: 'already_won' };
  }
  return { ok: true, completed, partial, winnerSide, played };
}

/** Games credited when a retirement decides the match: the winner is awarded the full match win. */
export function creditedGames(
  playedWinnerGames: number,
  playedLoserGames: number,
  bestOf: number,
): { winnerGames: number; loserGames: number } {
  void playedWinnerGames;
  return { winnerGames: Math.ceil(bestOf / 2), loserGames: playedLoserGames };
}

/**
 * Whether a stored game is a game: every row but a retired match's unfinished game
 * (`status 'in_progress'`, which exists only for a retirement). A row read without its status
 * (an older select) is a completed game.
 */
export function isCompletedGame(game: { status?: string | null }): boolean {
  return game.status !== 'in_progress';
}

/** A played game of a retired match: `completed` is false only for its unfinished game. */
export interface PlayedGame {
  player1: number;
  player2: number;
  completed: boolean;
}

/**
 * The completed games each side won in a retired match: the row's `player_*_games` when both
 * are recorded, else a tally of the completed games (the unfinished game never counts). The one
 * played-count rule for the standings credit and the rating input.
 */
export function retiredPlayedCounts(
  row: { player_1_games?: number | null; player_2_games?: number | null } | null | undefined,
  games: ReadonlyArray<PlayedGame>,
): { player1: number; player2: number } {
  if (row?.player_1_games != null && row?.player_2_games != null) {
    return { player1: row.player_1_games, player2: row.player_2_games };
  }
  const won = { player1: 0, player2: 0 };
  for (const g of games) {
    if (!g.completed) continue;
    if (g.player1 > g.player2) won.player1++;
    else if (g.player2 > g.player1) won.player2++;
  }
  return won;
}

/** The format columns a stored match may carry; any of them may be missing on a read. */
export interface StoredMatchFormat {
  best_of?: number | null;
  points_per_game?: number | null;
  deciding_game_points?: number | null;
  deuce_rule?: string | null;
}

/** The game target when a row does not say: the usual 11-point game. */
const DEFAULT_POINTS_PER_GAME = 11;
/** `match.best_of`'s column default, used for a retirement's credit when the row has none. */
const DEFAULT_BEST_OF = 5;

/**
 * Whether the last game of a retired match, read without per-game status (a score array), is its
 * unfinished game: not a valid completed game under the match's format. With no points target on
 * the row, it is unfinished only when neither side reached the usual 11.
 */
export function isUnfinishedLastGame(
  game: PlainGame,
  gameNumber: number,
  format: StoredMatchFormat,
): boolean {
  if (format.points_per_game == null) {
    return Math.max(game.player1, game.player2) < DEFAULT_POINTS_PER_GAME;
  }
  const target = pointsForGame(
    {
      bestOf: format.best_of ?? DEFAULT_BEST_OF,
      pointsPerGame: format.points_per_game,
      decidingGamePoints: format.deciding_game_points ?? null,
    },
    gameNumber,
  );
  const deuceRule = isDeuceRule(format.deuce_rule) ? format.deuce_rule : undefined;
  return !validateGameScore(game.player1, game.player2, target, deuceRule).valid;
}

/**
 * Game counts by side for a match a retirement decided (pool standings and rating input): the
 * winner is credited the full match (`creditedGames`) and the retiree keeps the completed games
 * they won. A row without a usable best_of is credited as the column default, best of 5; the
 * winner is always credited at least one game more than the retiree.
 */
export function retirementCredit(
  played: { player1: number; player2: number },
  retiredSide: Side,
  bestOf: number | null | undefined,
): { player1: number; player2: number } {
  const winnerSide: Side = retiredSide === 'player1' ? 'player2' : 'player1';
  const loserGames = played[retiredSide];
  const format = bestOf && bestOf > 0 ? bestOf : DEFAULT_BEST_OF;
  const credited = creditedGames(played[winnerSide], loserGames, format).winnerGames;
  const winnerGames = Math.max(credited, loserGames + 1);
  return winnerSide === 'player1'
    ? { player1: winnerGames, player2: loserGames }
    : { player1: loserGames, player2: winnerGames };
}

/** The match fields the rating input reads. */
export interface RatedMatch {
  player_1_id: string | null;
  player_2_id: string | null;
  retired_player_id?: string | null;
  best_of?: number | null;
  player_1_games?: number | null;
  player_2_games?: number | null;
  match_game?: ReadonlyArray<{
    player_1_score: number;
    player_2_score: number;
    status?: string | null;
  }> | null;
}

/**
 * Elo input for a retired match, from player 1's side: the credited game counts
 * (`retirementCredit`) as one-point games, so the winner always scores a win, even when the
 * retiree led in games. Null when no one retired (rate from the games as played). The played
 * counts are the row's `player_*_games` (completed games won), else its completed rows.
 */
export function retirementRatingGames(match: RatedMatch): Array<[number, number]> | null {
  const retired = match.retired_player_id;
  if (!retired) return null;
  const retiredSide: Side | null =
    retired === match.player_1_id ? 'player1' : retired === match.player_2_id ? 'player2' : null;
  if (!retiredSide) return null;
  const played = retiredPlayedCounts(
    match,
    (match.match_game ?? []).map((g) => ({
      player1: g.player_1_score,
      player2: g.player_2_score,
      completed: isCompletedGame(g),
    })),
  );
  const credit = retirementCredit(played, retiredSide, match.best_of);
  return [
    ...Array.from({ length: credit.player1 }, () => [1, 0] as [number, number]),
    ...Array.from({ length: credit.player2 }, () => [0, 1] as [number, number]),
  ];
}

type StoredFormat = Pick<
  Tables<'match'>,
  'best_of' | 'points_per_game' | 'deciding_game_points' | 'deuce_rule'
>;

const isBestOf = (n: unknown): n is GameFormat['bestOf'] => n === 1 || n === 3 || n === 5;
const isDeuceRule = (r: unknown): r is NonNullable<GameFormat['deuceRule']> =>
  r === 'win_by_2' || r === 'sudden_death';

/**
 * The format a retirement is checked against: the same one the ordinary finish accepts.
 * A pinned deciding target means the stored row (the deciding-format trigger keeps the scorer
 * settings equal to it). Otherwise the scorer may have changed the format, so its settings
 * apply: bestOf and pointsPerGame must be valid, and a missing deuceRule falls back to the row.
 * Null refuses. Mirrored by the retirement branch of SQL `scorer_finalize_core`.
 */
export function retirementFormat(
  match: Partial<StoredFormat>,
  settings: Partial<GameFormat> | null | undefined,
): GameFormat | null {
  if (match.deciding_game_points != null) {
    const { best_of, points_per_game, deuce_rule } = match;
    if (!isBestOf(best_of) || points_per_game == null || !isDeuceRule(deuce_rule)) return null;
    return {
      bestOf: best_of,
      pointsPerGame: points_per_game,
      decidingGamePoints: match.deciding_game_points,
      deuceRule: deuce_rule,
    };
  }
  const bestOf = settings?.bestOf;
  const pointsPerGame = settings?.pointsPerGame;
  const deuceRule = settings?.deuceRule ?? match.deuce_rule;
  if (!isBestOf(bestOf) || !isValidPointsPerGame(pointsPerGame) || !isDeuceRule(deuceRule))
    return null;
  return { bestOf, pointsPerGame, decidingGamePoints: null, deuceRule };
}

/**
 * The retirement verdict for a scorer app_state; null when no player retired. The retiree is
 * mapped to a side through `state.players`; the games are the completed games plus the
 * current (partial) game, which `validateRetiredGames` drops while it is still 0-0.
 */
export function validateRetirementState(
  state: ScoreAppState,
  format: GameFormat,
): RetiredVerdict | null {
  const retired = state.matchState?.retiredPlayer;
  if (!retired) return null;
  const index = (state.players ?? []).findIndex((p) => p.id === retired);
  if (index !== 0 && index !== 1) return { ok: false, reason: 'invalid_game' };
  const games: PlainGame[] = (state.matchState.games ?? []).map((g) => ({
    player1: g.player1,
    player2: g.player2,
  }));
  const current = state.matchState.currentScore;
  games.push({ player1: current?.player1 ?? 0, player2: current?.player2 ?? 0 });
  return validateRetiredGames(games, format, index === 0 ? 'player1' : 'player2');
}

export type RetirementRecord = Extract<RetiredVerdict, { ok: true }> & {
  /** The format the retirement was validated against (`retirementFormat`). */
  format: GameFormat;
};

/**
 * The validated retirement for a finalize: null when no player retired, `'invalid'` when the
 * retirement is refused (no usable format, an invalid score, a winner who is the retiree, or a
 * replayed state that differs from the verdict). The one check both finalize paths run.
 *
 * The verdict reads `matchState.games` + `currentScore`; the stored rows come from the replayed
 * state (`resolveAppState`: completed games, then the live game from `gameActions`). They must
 * agree, or the rows written would not be the retirement that was validated.
 */
export function resolveRetirement(
  match: Partial<StoredFormat>,
  state: ScoreAppState,
): RetirementRecord | 'invalid' | null {
  if (!state.matchState?.retiredPlayer) return null;
  const format = retirementFormat(match, state.matchSettings);
  const verdict = format ? validateRetirementState(state, format) : null;
  if (!format || !verdict?.ok || state.matchState.winner !== verdict.winnerSide) return 'invalid';
  const resolved = tryResolveAppState(state);
  const live = resolved?.current_game;
  const liveScore = live ? { player1: live.player_1_score, player2: live.player_2_score } : null;
  const livePlayed = !!liveScore && liveScore.player1 + liveScore.player2 > 0;
  if (
    !resolved ||
    resolved.games.filter((g) => g.completed).length !== verdict.completed.length ||
    (verdict.partial
      ? liveScore?.player1 !== verdict.partial.player1 ||
        liveScore?.player2 !== verdict.partial.player2
      : livePlayed)
  )
    return 'invalid';
  return { ...verdict, format };
}
